/* eslint-disable style/multiline-ternary */
'use client';

import type { EmojiClickData } from 'emoji-picker-react';
import { db } from '@/firebase';
import env from '@/utils/loadEnv';

import { getI18nPath } from '@/utils/Helpers';
import { IMAGE_CONSTRAINTS, resizeImageClient } from '@/utils/imageUtils';

import { ensureHttps } from '@/utils/urlUtils';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { onValue, push, ref, remove, set, update } from 'firebase/database';
import { Ban, Crown, ExternalLink, ImageIcon, Info, MoreHorizontal, Smile, Trash2, X } from 'lucide-react';
import { IonAvatar, IonButton, IonIcon, IonImg } from '@ionic/react';
import { IonRouterLink } from '@ionic/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { TextChat } from '../ui/textchat';
import { useTranslation } from 'react-i18next';
import { getLocale } from '@/lib/utils';
import { arrowBack } from 'ionicons/icons';
import { timeAgo } from '@/utils/timeAgo';
import { Avatar, AvatarImage, AvatarFallback } from '@radix-ui/react-avatar';
import { getInitialTheme } from '@/utils/theme';
import { GolfAvatar } from '../ui/GolfAvatar';
import GolfIcon from '../GolfIcon';
import { api } from '@/utils/api';

type ChatViewProps = {
  userId: string;
  activeChat: string | null;
  goBack: () => void;
};

type Message = {
  id: string;
  idn: string;
  sender: string;
  text: string;
  timestamp: string;
  linkPreview?: LinkPreview;
  imageUrl?: string;
  reactions?: { [userId: string]: string };
};

type ChatDetails = {
  type: 'group' | 'direct';
  name?: string;
  imageUrl?: string;
  participants: string[];
  createdBy?: string;
  createdAt?: string;
};

type LinkPreview = {
  title: string;
  description: string;
  image?: string;
  url: string;
};

type ChatParticipant = {
  id: string;
  name: string;
  avatar: string;
  username: string;
  online?: boolean;
  lastSeen?: string;
};
export default function ChatView({ userId, activeChat, goBack }: ChatViewProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatDetails, setChatDetails] = useState<ChatDetails | null>(null);
  const [participants, setParticipants] = useState<{ [key: string]: ChatParticipant }>({});
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null); // Store messageId
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const locale = getLocale();
  const { t } = useTranslation();
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const emojiPickerTheme = getInitialTheme();

  // Get other user for direct chats
  const otherUser = useMemo(() => {
    if (!chatDetails || chatDetails.type !== 'direct' || !participants) {
      return null;
    }
    const otherUserId = chatDetails.participants.find(id => id !== userId);
    return otherUserId ? participants[otherUserId] : null;
  }, [chatDetails, participants, userId]);
  // Fetch chat details and participants
  useEffect(() => {
    if (!activeChat) {
      setChatDetails(null);
      setParticipants({});
      return;
    }

    const chatRef = ref(db, `chats/${activeChat}`);
    const unsubscribeChat = onValue(chatRef, async (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        return;
      }

      setChatDetails({
        type: data.type,
        participants: data.participants || [],
        createdBy: data.createdBy,
        createdAt: data.createdAt,
      });

      // Fetch all participants' data
      const participantPromises = data.participants.map((participantId: string) => {
        const userRef = ref(db, `users/${participantId}`);
        return new Promise((resolve) => {
          onValue(userRef, (userSnapshot) => {
            const userData = userSnapshot.val();
            if (userData) {
              resolve({
                id: participantId,
                name: userData.name || t('unknown_user'),
                avatar: userData.avatar || '/assets/images/fallback.jpg',
                username: userData.username,
                online: userData.online || false,
                lastSeen: userData.lastSeen || '',
              });
            }
          });
        });
      });

      const participantData = await Promise.all(participantPromises);
      const participantMap = participantData.reduce((acc, participant: any) => {
        acc[participant.id] = participant;
        return acc;
      }, {});

      setParticipants(participantMap);
    });

    return () => unsubscribeChat();
  }, [activeChat, t]);

  // Automatically adjust height based on content
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';

      const newHeight = Math.min(Math.max((textareaRef.current.scrollHeight), 32), 240);

      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [newMessage]);

  useEffect(() => {
    const handleResize = () => {
      const isKeyboardOpen = window.innerHeight < document.documentElement.clientHeight;
      if (isKeyboardOpen) {
        setKeyboardHeight(document.documentElement.clientHeight - window.innerHeight);
      } else {
        setKeyboardHeight(0);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!activeChat || !userId) {
      return;
    }

    // Fetch messages
    const messagesRef = ref(db, `messages/${activeChat}`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const messageList = Object.entries(data)
          .map(([id, message]: [string, any]) => ({
            id,
            ...message,
          }))
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        setMessages(messageList);
        // Scroll to bottom when new messages arrive
        setTimeout(scrollToBottom, 100);
      } else {
        setMessages([]);
      }
    });

    return () => unsubscribe();
  }, [activeChat, userId]);

  useEffect(() => {
    if (!activeChat) {
      return;
    }

    const chatsRef = ref(db, 'chats');
    const now = new Date().getTime();

    // Fetch chats and delete inactive ones without messages
    onValue(chatsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        Object.entries(data).forEach(([chatId, chatDetails]: any) => {
          const chatAge = now - new Date(chatDetails.createdAt).getTime();
          if (
            chatId !== activeChat // Exclude the currently active chat
            && !chatDetails.lastMessage // Only delete chats without messages
            && chatAge > 5 * 1000 // Chat is older than 5 seconds
          ) {
            remove(ref(db, `chats/${chatId}`));
          }
        });
      }
    });
  }, [activeChat]);

  useEffect(() => {
    if (!activeChat || !userId) {
      return;
    }

    // Mark chat as read when opened
    const chatRef = ref(db, `chats/${activeChat}/readBy/${userId}`);
    set(chatRef, new Date().toISOString());
  }, [activeChat, userId]);

  const fetchOgData = async (url: string) => {
    const response = await api.get<LinkPreview>(`/sites?url=${encodeURIComponent(url)}`);
    return response.data;
  };

  const sendMessage = async () => {
    if (newMessage.trim() && activeChat) {
      // Ensure message is a string before matching URLs
      const urlRegex = /(https?:\/\/\S+)/g;
      const urls = newMessage.match(urlRegex);

      let linkPreview = null;

      // Url preview
      if (urls && urls.length > 0) {
        const secureUrl = ensureHttps(urls[0]); // Ensure the URL is secure
        try {
          const ogData = await fetchOgData(secureUrl); // Fetch from the API route
          linkPreview = {
            title: ogData.ogTitle || 'No title available',
            description: ogData.ogDescription || 'No description available',
            image: (ogData.ogImage && ogData.ogImage[0]) ? ogData.ogImage[0].url : '',
            url: secureUrl,
          };
        } catch (error) {
          console.error('Error fetching link preview:', error);
        }
      }

      const messageRef = ref(db, `messages/${activeChat}`);
      const chatRef = ref(db, `chats/${activeChat}`);

      const messageData = {
        sender: userId,
        text: newMessage,
        timestamp: new Date().toISOString(),
        linkPreview,
      };

      push(messageRef, messageData);

      await update(chatRef, {
        lastMessage: messageData.text,
        lastMessageTime: messageData.timestamp,
      });
      setNewMessage('');
      setShowEmojiPicker(false);
      // Scroll to bottom after sending
      setTimeout(scrollToBottom, 100);
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setNewMessage(prev => prev + emojiData.emoji); // Append the selected emoji to the input
    setShowEmojiPicker(false); // Close the emoji picker after selecting an emoji
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleReaction = async (emojiData: EmojiClickData, messageId: string) => {
    if (!activeChat) {
      return;
    }

    try {
      const reactionRef = ref(db, `messages/${activeChat}/${messageId}/reactions/${userId}`);
      await set(reactionRef, emojiData.emoji);
      setShowReactionPicker(null); // Close picker after selecting
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' })}`;
  };

  const renderEmoji = (text: string) => {
    return <span className="mb-2 text-5xl">{text}</span>; // Keep emoji styling as is
  };

  const renderLinkPreview = (linkPreview: any) => {
    return (
      <a href={linkPreview.url} target="_blank" rel="noopener noreferrer" className="flex max-w-60 flex-col rounded-lg bg-green-600 p-1">
        {linkPreview.image && (
          <img src={linkPreview.image} alt="Link Preview" className="rounded-lg" />
        )}
        <h3 className="font-bold text-white">{linkPreview.title}</h3>
        <p className="text-white">{linkPreview.description}</p>
        {/* <p className="text-gray-200">{linkPreview.url}</p> */}
      </a>
    );
  };

  const renderText = (text: string) => {
    return <p>{text}</p>; // Render text as it is today
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !activeChat) {
      return;
    }

    try {
      // First resize client-side for preview/validation
      const resizedFile = await resizeImageClient(
        file,
        IMAGE_CONSTRAINTS.MAX_WIDTH,
        IMAGE_CONSTRAINTS.MIN_WIDTH,
        IMAGE_CONSTRAINTS.ASPECT_RATIO_RANGE,
      );

      // Create FormData and append the resized file
      const formData = new FormData();
      formData.append('file', resizedFile);

      // Send to API for server-side processing and R2 upload
      const response = await fetch(`/images/resize`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const { imageUrl } = await response.json();

      const messageRef = ref(db, `messages/${activeChat}`);
      const chatRef = ref(db, `chats/${activeChat}`);

      const messageData = {
        sender: userId,
        text: '',
        imageUrl,
        timestamp: new Date().toISOString(),
      };

      push(messageRef, messageData);
      await update(chatRef, {
        lastMessage: '[Image]',
        lastMessageTime: messageData.timestamp,
      });
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!activeChat) {
      return;
    }

    try {
      // Delete the message
      const messageRef = ref(db, `messages/${activeChat}/${messageId}`);
      await remove(messageRef);

      // If it was the last message, update the chat's lastMessage
      const remainingMessages = messages.filter(msg => msg.id !== messageId);
      const chatRef = ref(db, `chats/${activeChat}`);
      const lastMessage = remainingMessages[remainingMessages.length - 1]; // Get last remaining message

      if (lastMessage) {
        await update(chatRef, {
          lastMessage: lastMessage.text || '[Image]',
          lastMessageTime: lastMessage.timestamp,
        });
      } else {
        // If no messages left, clear last message data
        await update(chatRef, {
          lastMessage: null,
          lastMessageTime: null,
        });
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const renderMessage = (msg: Message) => {
    const isSingleEmoji = /^\p{Emoji}$/u.test(msg.text);
    const isOtherUser = msg.sender !== userId;
    const participant = participants[msg.sender];

    // Get unique reactions and their counts
    const reactionCounts = msg.reactions ? Object.values(msg.reactions).reduce((acc, emoji) => {
      acc[emoji] = (acc[emoji] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number }) : {};

    return (
      <div key={msg.idn} className={`group flex flex-col ${isOtherUser ? 'items-start' : 'items-end'}`}>
        {isOtherUser && (
          <p className="mb-1 ml-10 text-xs font-semibold text-primary">
            {participant?.name || 'Unknown User'}
          </p>
        )}
        <div className={`relative flex gap-2 max-w-[70%] ${isOtherUser ? 'justify-start' : 'justify-end'} w-full`}>
          {isOtherUser && (
            <GolfAvatar size={40} imageUrl={participant?.avatar || '/assets/images/fallback.jpg'} />
          )}
          <div className="relative">
            <div
              className={`relative rounded-lg ${msg.linkPreview || msg.imageUrl ? 'p-1' : 'px-4 py-2'
                } ${isSingleEmoji || msg.imageUrl
                  ? ''
                  : isOtherUser
                    ? 'bg-gray-200 text-black dark:bg-neutral-700 dark:text-white'
                    : 'bg-primary text-white'
                }`}
            >
              {msg.imageUrl && (
                <IonAvatar>
                  <img
                    src={msg.imageUrl}
                    alt="Chat image"
                    className="object-cover p"
                  />
                </IonAvatar>
              )}
              {msg.linkPreview && renderLinkPreview(msg.linkPreview)}
              {msg.text && (isSingleEmoji ? renderEmoji(msg.text) : renderText(msg.text))}
              <p className={`mt-1 text-right text-xs ${msg.sender === userId && !(isSingleEmoji || msg.imageUrl) ? 'text-white' : 'text-foreground'}`}>
                {formatTimestamp(msg.timestamp)}
              </p>

              {/* Reactions Display */}
              {Object.keys(reactionCounts).length > 0 && (
                <div className={`absolute ${isOtherUser ? 'right-0 translate-x-1/4' : 'left-0 -translate-x-1/4'} bottom-0 flex translate-y-1/2 -space-x-1 rounded-full bg-card px-2 py-0.5 shadow-md`}>
                  {Object.entries(reactionCounts).map(([emoji, count]) => (
                    <div key={emoji} className="flex items-center">
                      <span className="text-sm">{emoji}</span>
                      {count > 1 && (
                        <span className="ml-1 text-xs text-muted-foreground">{count}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Updated Delete Button in Action Buttons */}
            <div
              className={`invisible absolute top-1/2 flex -translate-y-1/2 gap-1 rounded-full bg-background p-1 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 ${isOtherUser ? 'left-[calc(100%+8px)]' : 'right-[calc(100%+8px)]'
                }`}
            >
              {/* Reaction Picker */}
              <div className="relative justify-center">
                <div className="absolute bottom-full z-50 mb-2 translate-x-[-45%]">
                  <EmojiPicker
                    theme={emojiPickerTheme == 'light' ? Theme.LIGHT : Theme.DARK}
                    open={showReactionPicker === msg.id}
                    reactionsDefaultOpen
                    onEmojiClick={emojiData => handleReaction(emojiData, msg.id)}
                    searchDisabled
                    skinTonesDisabled
                    allowExpandReactions={false}
                    previewConfig={{ showPreview: false }}
                    height={150}
                    width={250}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id)}
                >
                  <Smile className="size-5" />
                </Button>
              </div>
              {/* {msg.text ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => {
                    navigator.clipboard.writeText(msg.text);
                  }}
                >
                  <Forward className="size-5" />
                </Button>
              ) : ''} */}
              {msg.sender === userId && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-destructive hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (msg.id) {
                      deleteMessage(msg.id);
                    }
                  }}
                >
                  <Trash2 className="size-5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => {
                  // Add reply handler
                }}
              >
                <MoreHorizontal className="size-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add useEffect for handling clicks outside emoji picker
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current;
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }
  };

  return (
    <div className="flex h-full flex-1">
      {/* Main Chat Area */}
      <div className={`inset-0 flex flex-1 flex-col ${showInfoPanel ? 'lg:w-2/3' : 'w-full'}`}>
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div className="flex h-16 items-center justify-between border-b bg-card p-3">
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={goBack}
                  className="static text-black dark:text-gray-400 lg:hidden"
                >
                  <IonIcon icon={arrowBack} className="size-6" />
                </Button>
                <IonButton className="relative rounded-lg space-x-2 text-sm" routerLink={`/u/${otherUser?.username}`} fill='clear'>
                  <Avatar className="size-10 rounded-full">
                    <AvatarImage className="size-10 rounded-full object-cover" height={64} width={64} src={
                      chatDetails?.type === 'direct'
                        ? otherUser?.avatar || '/assets/images/fallback.jpg'
                        : '/assets/images/fallback.jpg'
                    } alt="avatar picture" />
                    <AvatarFallback className="size-16 rounded-full">{(otherUser?.name)}</AvatarFallback>
                  </Avatar>
                  <div className='flex flex-col text-foreground text-start justify-start'>
                    <h2 className="font-semibold">
                      {chatDetails?.type === 'direct'
                        ? otherUser?.name || t('unknown_user')
                        : chatDetails?.name ? chatDetails.name : t('group_chat')}
                    </h2>
                    {chatDetails?.type === 'direct' && otherUser?.online && (
                      <span className="absolute right-0 size-3 rounded-full border-2 border-gray-900 bg-green-500 bottom-safe"></span>
                    )}
                    {otherUser?.online ? t('online') : timeAgo(new Date(otherUser?.lastSeen || 0), t)}
                  </div>
                </IonButton>
              </div>
              <button
                type="button"
                onClick={() => setShowInfoPanel(!showInfoPanel)}
                className="text-black hover:text-primary dark:text-gray-400"
              >
                <Info className="size-6" />
              </button>
            </div>

            {/* Messages Area */}
            <ScrollArea
              ref={scrollAreaRef}
              className="flex-1 space-y-4 overflow-y-auto overflow-x-hidden p-4"
              style={{ paddingBottom: `${keyboardHeight + 16}px` }}
            >
              {messages.map(renderMessage)}
            </ScrollArea>

            {/* Floating Input Area */}
            <div className="relative pb-safe">
              <div
                className="m-2 flex items-start rounded-lg border bg-background"
                style={{ marginBottom: keyboardHeight > 8 ? keyboardHeight : 8 }}
              >
                <div className="absolute right-2 z-50 bottom-safe-offset-14" ref={emojiPickerRef}>
                  <EmojiPicker
                    theme={emojiPickerTheme}
                    open={showEmojiPicker}
                    onEmojiClick={handleEmojiClick}
                    previewConfig={{ showPreview: false }}
                    searchDisabled
                    skinTonesDisabled
                  />
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                />
                <TextChat
                  ref={textareaRef}
                  value={newMessage}
                  maxLength={2058}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault(); // Prevent default line break
                      if (!e.shiftKey) {
                        sendMessage();
                      } else {
                        // Allow Shift+Enter for line breaks
                        setNewMessage(prev => `${prev}\n`);
                      }
                    }
                  }}
                  placeholder={t('send_message_placeholder')}
                  className="max-h-[320px] min-h-8 resize-none border-0 bg-transparent p-2 placeholder:text-gray-400 focus-visible:ring-0"
                  style={{
                    overflow: newMessage.split('\n').length >= 6 ? 'auto' : 'hidden',
                  }}
                />
                <div className="mx-2 flex items-center">
                  <button
                    type="button"
                    className="rounded-full p-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <ImageIcon className="size-6" />
                  </button>
                  <button
                    type="button"
                    className="rounded-full p-2"
                    onClick={() => setShowEmojiPicker(prev => !prev)}
                  >
                    <Smile className="size-6" />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center">
            <GolfIcon name="message-multiple" className="size-32 brightness-50" />
            <p className="mt-4 text-gray-400">{t('select_chat_placeholder')}</p>
          </div>
        )}
      </div>

      {/* User Information Panel */}
      {showInfoPanel && (
        <div className="absolute bg-card inset-0 z-50 flex min-w-80 flex-col border-l lg:static lg:w-1/4">
          {/* Header */}
          <div className="flex h-16 items-center justify-between border-b p-3">
            <div className="text-lg font-semibold">
              {chatDetails?.type === 'direct' ? t('user_information') : t('group_information')}
            </div>
            <button
              type="button"
              onClick={() => setShowInfoPanel(false)}
              className="text-black hover:text-primary dark:text-gray-400"
            >
              <X className="size-6" />
            </button>
          </div>

          {/* Content Area - Flex container */}
          <div className="flex h-full flex-col">
            {/* Top Section */}
            <div className="shrink-0">
              {/* Centered Avatar and Name */}
              <div className="mt-6 flex flex-col items-center">
                <div className="relative">
                  <IonAvatar className='size-32'>
                    <img
                      src={
                        chatDetails?.type === 'direct'
                          ? otherUser?.avatar || '/assets/images/fallback.jpg'
                          : chatDetails?.imageUrl || '/assets/images/fallback.jpg'
                      }
                      alt={otherUser?.name || 'Participant Avatar'}
                      className="size-32 object-cover"
                    />
                  </IonAvatar>

                  {chatDetails?.type === 'direct' && otherUser?.online && (
                    <span className="absolute right-0 size-3 rounded-full border-2 border-gray-900 bg-green-500 bottom-safe"></span>
                  )}

                </div>
                <h3 className="p-3 text-lg font-semibold">
                  {chatDetails?.type === 'direct'
                    ? otherUser?.name || t('unknown_user')
                    : chatDetails?.name
                      ? chatDetails.name
                      : chatDetails?.participants
                        .slice(0, 3)
                        .map(id => participants[id]?.name || t('unknown_user'))
                        .join(', ')}
                </h3>
                {chatDetails?.type === 'direct' && (
                  <p className="text-sm text-gray-400">{otherUser?.online ? t('online') : t('offline')}</p>
                )}
              </div>

              {/* Members List for Group Chats */}
              {chatDetails?.type === 'group' && (
                <div className="p-4">
                  <h4 className="mb-4 font-semibold">
                    {t('members')}
                    {' '}
                    (
                    {chatDetails.participants.length}
                    )
                  </h4>
                  <div className="space-y-4">
                    {chatDetails.participants.map((participantId) => {
                      const participant = participants[participantId];
                      const isAdmin = chatDetails.createdBy === participantId;

                      return (
                        <div key={participantId} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <IonImg
                                src={participant?.avatar || '/assets/images/fallback.jpg'}
                                alt={participant?.name || t('unknown_user')}
                                className="size-8 rounded-full object-cover"
                              />
                              {participant?.online && (
                                <span className="absolute right-0 size-2 rounded-full border border-background bg-green-500 bottom-safe" />
                              )}
                            </div>
                            <span className="flex items-center justify-center align-middle text-sm">
                              {participant?.name || t('unknown_user')}
                              {isAdmin && (
                                <Crown fill="#eab308" className="size-5 pl-1 text-yellow-500" />
                              )}
                            </span>
                          </div>
                          <IonRouterLink href={getI18nPath(`/${participant?.username}`, locale)} className="contents">
                            <Button variant="ghost" size="icon" type="button">
                              <ExternalLink className="size-5" />
                            </Button>
                          </IonRouterLink>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons - Pushed to bottom */}
            <div className="mt-auto space-y-2 border-t p-4">
              {chatDetails?.type === 'direct' ? (
                <>
                  <IonRouterLink href={getI18nPath(`/${otherUser?.username}`, locale)} className="contents">
                    <Button variant="ghost" type="button" className="flex w-full gap-3 p-4 hover:bg-secondary">
                      <ExternalLink className="size-5" />
                      {t('see_profile')}
                    </Button>
                  </IonRouterLink>
                  <Button variant="ghost" type="button" className="flex w-full gap-3 p-4 hover:bg-secondary">
                    <Ban className="size-5" />
                    {t('block_user')}
                  </Button>
                </>
              ) : null}
              <Button variant="ghost" type="button" className="flex w-full gap-3 p-4 text-red-500 hover:bg-secondary hover:text-red-600">
                <Trash2 className="size-5" />
                {t('delete_chat')}
              </Button>
            </div>
          </div>
        </div>
      )
      }
    </div >
  );
}
